import React, { useContext, useState, useEffect } from "react";
import { CartContext } from "../context/CartContext";
import { createRazorpayOrder, createOrder, getImageUrl } from "../api/api";
import { useNavigate } from "react-router-dom";
import "./Checkout.css"; 

function Checkout() {
  const { cart, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const [addressSaved, setAddressSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState({
    name: "",
    phone: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    country: "IN"
  });

  const normalizeAddress = (saved) => {
    if (!saved || typeof saved !== "object") return null;
    return {
      name: saved.name || "",
      phone: saved.phone || "",
      email: saved.email || "",
      address1: saved.address1 || saved.fullAddress || "",
      address2: saved.address2 || "",
      city: saved.city || "",
      state: saved.state || "",
      pincode: saved.pincode || saved.pin || saved.postcode || "",
      country: "IN"
    };
  };

  const getDigits = (value) => String(value || "").replace(/\D/g, "");

  const getTenDigitMobile = (value) => {
    const digits = getDigits(value);
    if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
    return digits;
  };

  const validateAddress = (addr) => {
    if (!addr.name || !addr.name.trim()) return "Please enter your full name.";
    const mobile = getTenDigitMobile(addr.phone);
    if (!/^[6-9]\d{9}$/.test(mobile)) return "Please enter a valid 10-digit Indian mobile number.";
    if (!addr.email || !addr.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr.email.trim())) return "Please enter a valid email address.";
    if (!addr.address1 || !addr.address1.trim()) return "Please enter Address Line 1 (House, Building, Street).";
    if (!addr.city || !addr.city.trim()) return "Please enter your city.";
    if (!addr.state || !addr.state.trim()) return "Please enter your state.";
    if (!/^\d{6}$/.test(String(addr.pincode || "").trim())) return "Please enter a valid 6-digit PIN code.";
    return "";
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(`address_data`);
      if (saved) {
        const parsed = JSON.parse(saved);
        const normalized = normalizeAddress(parsed);
        if (normalized) {
          setAddress(normalized);
          // Old saved addresses may only contain fullAddress; require
          // structured fields to be completed before marking as saved.
          if (!validateAddress(normalized)) {
            setAddressSaved(true);
          } else {
            setAddressSaved(false);
          }
        }
      }
    } catch (e) {
      // Corrupt saved address: ignore and let customer fill the form.
    }
  }, []);

  // ✅ 1. Sabse pehle Full Subtotal calculate karein
  const subtotal = cart?.reduce((acc, item) => {
    const price = Number(item.price || (item.prices?.price / 100) || 0);
    const qty = Number(item.quantity || item.qty || 1);
    return acc + (price * qty);
  }, 0) || 0;

  const handlePayment = async () => {
    if (!addressSaved) return alert("Please save shipping details first!");

    const addressError = validateAddress(address);
    if (addressError) {
      setAddressSaved(false);
      return alert(addressError);
    }
    
    setLoading(true);
    try {
      // ✅ 2. Yahan subtotal bhej rahe hain (Full Payment)
      // Note: Backend handle karega subtotal * 100 for Paise
      const rzpOrder = await createRazorpayOrder(subtotal);
      
      const options = {
        key: "rzp_live_SWAlpgcVwQoOoW", 
        amount: rzpOrder.amount, // Ye backend se aayega (Subtotal in Paise)
        order_id: rzpOrder.id,
        name: "Lacegota",
        description: "Full Payment for Order",
        image: "https://your-logo-url.com/logo.png", // Apna logo lagayein
        handler: async (res) => {
          setLoading(true);
          try {
            const orderData = {
              payment_id: res.razorpay_payment_id,
              transaction_id: res.razorpay_order_id,
              status: "processing", 
              set_paid: true, 
              billing: {
                first_name: address.name.trim(),
                address_1: address.address1.trim(),
                address_2: (address.address2 || "").trim(),
                city: address.city.trim(),
                state: address.state.trim(),
                postcode: String(address.pincode || "").trim(),
                country: "IN",
                phone: String(address.phone || "").trim(),
                email: String(address.email || "").trim()
              },
              shipping: {
                first_name: address.name.trim(),
                address_1: address.address1.trim(),
                address_2: (address.address2 || "").trim(),
                city: address.city.trim(),
                state: address.state.trim(),
                postcode: String(address.pincode || "").trim(),
                country: "IN"
              },
              line_items: cart.map(item => ({ 
                product_id: item.id, 
                quantity: Number(item.quantity || item.qty) 
              })),
              meta_data: [
                { key: "_payment_method", value: "Razorpay Full Online" },
                { key: "_order_total", value: subtotal.toString() }
              ]
            };
            
            await createOrder(orderData); 

            localStorage.setItem("userName", address.name);
            localStorage.setItem("userEmail", address.email);

            const newOrder = {
              id: res.razorpay_payment_id,
              status: 'processing',
              total: subtotal.toFixed(2),
              line_items: [...cart],
              date: new Date().toLocaleString()
            };

            const existing = JSON.parse(localStorage.getItem("orders_history")) || [];
            localStorage.setItem("orders_history", JSON.stringify([...existing, newOrder]));

            await clearCart(); 
            navigate("/orders");
          } catch (err) {
            alert("Order synchronization failed. Please contact support.");
          } finally {
            setLoading(false);
          }
        },
        prefill: { name: address.name, email: address.email, contact: address.phone },
        theme: { color: "#232f3e" } // Amazon dark theme color
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      alert("Could not initiate payment. Check connection.");
      setLoading(false);
    }
  };

  return (
    <div className="checkout-container">
      <h1 className="checkout-title">Secure Checkout</h1>
      <div className="checkout-layout">
        <div className="checkout-main">
          
          {/* Section 1: Address */}
          <div className="checkout-card">
            <h3 className="section-step">1. Delivery Address</h3>
            {!addressSaved ? (
              <div className="address-form">
                <input placeholder="Full Name" onChange={e=>setAddress({...address, name:e.target.value})} value={address.name} />
                <input placeholder="Phone Number" onChange={e=>setAddress({...address, phone:e.target.value})} value={address.phone} />
                <input placeholder="Email Address" onChange={e=>setAddress({...address, email:e.target.value})} value={address.email} />
                <input placeholder="Address Line 1 / House, Building, Street" onChange={e=>setAddress({...address, address1:e.target.value})} value={address.address1} />
                <input placeholder="Address Line 2 / Area, Landmark (Optional)" onChange={e=>setAddress({...address, address2:e.target.value})} value={address.address2} />
                <input placeholder="City" onChange={e=>setAddress({...address, city:e.target.value})} value={address.city} />
                <input placeholder="State" onChange={e=>setAddress({...address, state:e.target.value})} value={address.state} />
                <input placeholder="PIN Code" inputMode="numeric" maxLength={6} onChange={e=>setAddress({...address, pincode:e.target.value})} value={address.pincode} />
                <input value="India" disabled readOnly />
                <button className="amazon-btn" onClick={() => { 
                  const nextAddress = { ...address, country: "IN" };
                  const error = validateAddress(nextAddress);
                  if(!error) {
                    setAddress(nextAddress);
                    localStorage.setItem("address_data", JSON.stringify(nextAddress)); 
                    setAddressSaved(true); 
                  } else { alert(error); }
                }}>Use this address</button>
              </div>
            ) : (
              <div className="saved-address">
                <p><strong>{address.name}</strong></p>
                <p>{address.address1}{address.address2 ? `, ${address.address2}` : ""}</p>
                <p>{address.city}, {address.state} - {address.pincode}</p>
                <p>India</p>
                <p>Phone: {address.phone}</p>
                <button className="edit-link" onClick={()=>setAddressSaved(false)}>Change Address</button>
              </div>
            )}
          </div>

          {/* Section 2: Payment Method */}
          <div className="checkout-card">
            <h3 className="section-step">2. Payment Method</h3>
            <div className="payment-option-box selected">
                <div className="payment-radio-row">
                    <input type="radio" checked readOnly />
                    <label><strong>Prepaid: UPI / Cards / NetBanking</strong></label>
                </div>
                <p className="payment-subtext">Safe & Secure 256-bit encrypted payment via Razorpay</p>
            </div>
          </div>
        </div>

        {/* Sidebar Summary */}
        <div className="checkout-sidebar">
          <div className="checkout-card sticky-card">
            <div className="sidebar-header">
               <h3>Order Summary</h3>
            </div>
            <div className="summary-list">
              {cart.map(item => (
                <div key={item.cartKey || item.id} className="summary-item">
                  <div className="item-img-container">
                     <img src={getImageUrl(item.image || item.images?.[0]?.src || item.images?.[0])} alt="" />
                  </div>
                  <div className="item-details">
                     <p className="item-name">{item.name}</p>
                     <p className="item-qty">Qty: {item.quantity || item.qty}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="order-total-section">
                <div className="total-row-item">
                    <span>Items:</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="total-row-item">
                    <span>Delivery:</span>
                    <span className="free-text">FREE</span>
                </div>
                <div className="total-final-row">
                    <span>Order Total:</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                </div>
            </div>

            <button 
                className="pay-now-btn" 
                onClick={handlePayment} 
                disabled={loading || !addressSaved}
            >
              {loading ? "Processing..." : `Place Your Order and Pay`}
            </button>
            <p className="secure-text">🔒 Secure Transaction</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
